import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { 
  Calendar, 
  Clock, 
  Edit, 
  MoreHorizontal, 
  PenLine, 
  Trash, 
  UserCheck, 
  Users, 
  Archive, 
  CheckCircle2, 
  XCircle, 
  Eye,
  BookOpen,
  MapPin
} from "lucide-react";
import { cn, formatCEU } from "@/lib/utils";
import { Course } from "../types";
import { useNavigate } from "react-router-dom";
import { format } from 'date-fns';

interface CourseCardProps {
  course: Course;
  index: number;
  onStatusChange: (courseId: string, newStatus: string) => void;
  onDelete: (courseId: string) => void;
  onAttendance: (courseId: string) => void;
}

export const CourseCard = ({ 
  course, 
  index, 
  onStatusChange, 
  onDelete, 
  onAttendance 
}: CourseCardProps) => {
  const navigate = useNavigate();
  
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "";
    // Always extract the date part and format as local date
    const dateOnly = dateString.split('T')[0];
    const date = new Date(dateOnly + 'T12:00:00'); // Use noon to avoid timezone issues
    return format(date, 'MMMM d, yyyy');
  };

  return (
    <Card 
      className="overflow-hidden rounded-xl border-l-4 border-[#0f8f8c] bg-white shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-200 animate-fade-in"
      style={{ 
        borderLeftColor: course.event_type === 'live' ? '#004161' : '#0f8f8c',
        animationDelay: `${index * 50}ms` 
      }}
    >
      <CardContent className="p-0">
        <div className="flex flex-col h-full">
          {/* Header Row */}
          <div className="flex items-center justify-between px-4 pt-3 pb-1">
            <h3 className="font-title text-lg text-primary font-bold truncate max-w-[60%]">{course.title}</h3>
            <Badge 
              variant={
                course.status === 'published' ? 'accent' :
                course.status === 'draft' ? 'secondary' :
                course.status === 'archived' ? 'accent-light' : 'destructive'
              }
              className="ml-2 font-subtitle text-xs px-2 py-1"
            >
              {course.status.charAt(0).toUpperCase() + course.status.slice(1)}
            </Badge>
          </div>
          
          {/* Meta Row */}
          <div className="flex flex-wrap items-center gap-3 px-4 pb-2 text-xs text-primary/80">
            <Badge 
              variant={course.event_type === 'live' ? 'default' : 'accent'}
              className="font-subtitle px-2 py-0.5 text-xs"
            >
              {course.event_type === 'live' ? 'Live' : 'Self-Paced'}
            </Badge>
            {course.start_date && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDate(course.start_date)}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
                    {formatCEU(course.hours)} CEUs
                  </span>
            {course.max_participants && (
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {course.max_participants}
              </span>
            )}
                {course.category && (
              <span className="flex items-center gap-1">
                <BookOpen className="h-3 w-3" />
                      {course.category}
                    </span>
                )}
                {course.venue_details && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                      {course.venue_details}
                    </span>
                )}
          </div>
      
          {/* Actions Row */}
          <div className="flex items-center justify-end gap-2 px-4 pb-3">
          <Button 
              variant="ghost" 
              size="icon"
            onClick={() => navigate(`/admin/courses/${course.id}/attendance`)}
              className="hover:bg-accent-subtle"
              title="Attendance"
          >
              <UserCheck className="h-4 w-4 text-primary" />
          </Button>
          <Button 
              variant="ghost" 
              size="icon" 
            onClick={() => navigate(`/admin/courses/${course.id}`)}
              className="hover:bg-accent-subtle"
              title="Edit"
          >
              <Edit className="h-4 w-4 text-primary" />
          </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" title="More">
                  <MoreHorizontal className="h-4 w-4 text-primary" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="font-subtitle w-40">
            <DropdownMenuItem 
              onClick={() => navigate(`/courses/${course.id}`)}
              className="gap-2 text-xs"
            >
              <Eye className="h-3.5 w-3.5" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => onStatusChange(course.id, 'published')}
              className="gap-2 text-xs"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Publish
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => onStatusChange(course.id, 'draft')}
              className="gap-2 text-xs"
            >
              <PenLine className="h-3.5 w-3.5" />
              Move to Draft
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => onStatusChange(course.id, 'archived')}
              className="gap-2 text-xs"
            >
              <Archive className="h-3.5 w-3.5" />
              Archive
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => onStatusChange(course.id, 'cancelled')}
                  className="gap-2 text-xs"
            >
              <XCircle className="h-3.5 w-3.5" />
              Cancel
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => onDelete(course.id)}
              className="gap-2 text-xs text-destructive"
            >
              <Trash className="h-3.5 w-3.5" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
